import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Text from '@cartwhl/ui/base/TextNew';
import SearchIcon from '@cartwhl/ui/icons/SearchIcon';

import { useStore } from '../dashboardStore';
import type { DashboardWorkFilter, DashboardWorkTypeFilter } from '../types';
import { groupDashboardWorkItems } from '../workItemGrouping';
import DashboardWorkListError from './DashboardWorkListError';
import DashboardWorkListLoading from './DashboardWorkListLoading';
import DashboardLibraryPrompt from './DashboardLibraryPrompt';
import DashboardWorkSection from './DashboardWorkSection';

const WORK_FILTERS: Array<{ label: string; value: DashboardWorkFilter }> = [
  { label: 'My work', value: 'mine' },
  { label: 'Team work', value: 'team' },
];

const WORK_TYPE_FILTERS: Array<{
  label: string;
  value: DashboardWorkTypeFilter;
}> = [
  { label: 'All types', value: 'all' },
  { label: 'Cartwheel Scenes', value: 'scene' },
  { label: 'Canvas Projects', value: 'canvas-project' },
  { label: 'Comic Jobs', value: 'comic-job' },
  { label: 'Swing Jobs', value: 'swing-job' },
  { label: 'Mascot Jobs', value: 'mascot-job' },
];

export default function DashboardWorkList() {
  const dashboardStore = useStore();
  const {
    workItemsFilter,
    workItemsLoad,
    workItemsSearchQuery,
    workItemsTypeFilter,
  } = dashboardStore.state;
  const workItems = workItemsLoad.data?.items ?? [];
  const warningMessage = workItemsLoad.data?.warning;

  if (workItemsLoad.status === 'initial') {
    return null;
  }

  if (workItemsLoad.status === 'pending' && !workItemsLoad.data) {
    return <DashboardWorkListLoading />;
  }

  if (workItemsLoad.status === 'error') {
    return (
      <DashboardWorkListError
        message={workItemsLoad.error}
        onRetry={() => void dashboardStore.reloadWorkItems()}
      />
    );
  }

  const normalizedSearchQuery = workItemsSearchQuery.trim().toLowerCase();
  const filteredWorkItems = workItems.filter((item) => {
    if (item.scope !== workItemsFilter) return false;
    if (workItemsTypeFilter !== 'all' && item.kind !== workItemsTypeFilter) {
      return false;
    }
    if (!normalizedSearchQuery) return true;

    return item.title.toLowerCase().includes(normalizedSearchQuery);
  });
  const sections = groupDashboardWorkItems(filteredWorkItems);
  const filterHeader = (
    <DashboardWorkListHeader
      activeFilter={workItemsFilter}
      activeTypeFilter={workItemsTypeFilter}
      searchQuery={workItemsSearchQuery}
      onFilterChange={(filter) => dashboardStore.setWorkItemsFilter(filter)}
      onSearchQueryChange={(query) =>
        dashboardStore.setWorkItemsSearchQuery(query)
      }
      onTypeFilterChange={(filter) =>
        dashboardStore.setWorkItemsTypeFilter(filter)
      }
    />
  );

  if (sections.length === 0) {
    return (
      <section className="mx-auto flex w-full max-w-[1020px] flex-col gap-3">
        {filterHeader}
        <Text token="14/medium" className="text-secondary">
          {workItems.length === 0
            ? 'No recent work found.'
            : normalizedSearchQuery || workItemsTypeFilter !== 'all'
              ? 'No matching work found.'
              : workItemsFilter === 'mine'
                ? 'No recent personal work found.'
                : 'No recent team work found.'}
        </Text>
      </section>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1020px] flex-col gap-8">
      {filterHeader}
      {warningMessage ? (
        <Text token="14/medium" className="text-tertiary px-2 text-pretty">
          {warningMessage}
        </Text>
      ) : null}
      {sections.map((section, index) => (
        <DashboardWorkSection
          key={section.id}
          section={section}
          showDivider={index > 0}
        />
      ))}
      <DashboardLibraryPrompt />
    </div>
  );
}

interface DashboardWorkListHeaderProps {
  activeFilter: DashboardWorkFilter;
  activeTypeFilter: DashboardWorkTypeFilter;
  searchQuery: string;
  onFilterChange: (filter: DashboardWorkFilter) => void;
  onSearchQueryChange: (query: string) => void;
  onTypeFilterChange: (filter: DashboardWorkTypeFilter) => void;
}

function DashboardWorkListHeader({
  activeFilter,
  activeTypeFilter,
  searchQuery,
  onFilterChange,
  onSearchQueryChange,
  onTypeFilterChange,
}: DashboardWorkListHeaderProps) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        {WORK_FILTERS.map((filter) => {
          const isActive = filter.value === activeFilter;

          return (
            <button
              key={filter.value}
              type="button"
              aria-pressed={isActive}
              className={`flex items-center justify-center rounded-lg px-4 py-[11px] transition-colors ${
                isActive ? 'bg-secondary' : 'hover:bg-secondary'
              }`}
              onClick={() => onFilterChange(filter.value)}
            >
              <Text token="14/medium" className="text-primary text-center">
                {filter.label}
              </Text>
            </button>
          );
        })}
      </div>

      <div className="flex w-full items-center gap-4 sm:w-auto">
        <label className="bg-secondary flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg py-2.5 pr-2 pl-4 sm:w-[270px] sm:flex-none">
          <span className="sr-only">Search recent work titles</span>
          <Text
            as="input"
            token="14/medium"
            type="search"
            value={searchQuery}
            placeholder="Search work"
            className="placeholder:text-tertiary text-primary min-w-0 flex-1 bg-transparent outline-none"
            onChange={(event) => onSearchQueryChange(event.currentTarget.value)}
          />
          <SearchIcon className="text-primary size-5 flex-none" />
        </label>

        <Dropdown
          align="end"
          width={220}
          trigger={
            <button
              type="button"
              aria-label={`Filter work type: ${getWorkTypeFilterLabel(activeTypeFilter)}`}
              className={`border-primary text-primary hover:bg-secondary flex h-8 w-8 flex-none items-center justify-center rounded-lg border p-[5px] transition-colors ${
                activeTypeFilter === 'all' ? '' : 'bg-secondary'
              }`}
            >
              <FilterIcon className="size-5" />
            </button>
          }
        >
          {WORK_TYPE_FILTERS.map((filter) => {
            const isActive = filter.value === activeTypeFilter;

            return (
              <Dropdown.Item
                key={filter.value}
                icon={isActive ? <SelectedFilterIcon /> : null}
                onClick={() => onTypeFilterChange(filter.value)}
              >
                {filter.label}
              </Dropdown.Item>
            );
          })}
        </Dropdown>
      </div>
    </div>
  );
}

function getWorkTypeFilterLabel(filter: DashboardWorkTypeFilter) {
  return (
    WORK_TYPE_FILTERS.find((option) => option.value === filter)?.label ??
    'All types'
  );
}

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M4 5.75H16M6.5 10H13.5M8.5 14.25H11.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SelectedFilterIcon() {
  return (
    <svg
      viewBox="0 0 6 6"
      fill="currentColor"
      className="text-primary h-1.5 w-1.5"
      aria-hidden="true"
    >
      <circle cx="3" cy="3" r="3" />
    </svg>
  );
}
